import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Form,
    Modal,
    Nav,
    Pagination,
    Spinner,
    Table,
} from 'react-bootstrap';
import debounce from 'lodash.debounce';
import {
    addDndEmail,
    addDndPhone,
    DndListItem,
    DndListType,
    downloadDndCsv,
    getDndList,
    removeDndEmail,
    removeDndPhone,
} from './api';

const PAGE_SIZE = 100;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;

const TAB_CONFIG: { key: DndListType; label: string; column: string }[] = [
    { key: 'email', label: 'By Email', column: 'Email' },
    { key: 'phone', label: 'By Phone Number', column: 'Phone Number' },
];

const triggerCsvDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const DndList: React.FC = () => {
    const [activeTab, setActiveTab] = useState<DndListType>('email');
    const [items, setItems] = useState<DndListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');

    const [showAddModal, setShowAddModal] = useState(false);
    const [addValue, setAddValue] = useState('');
    const [addError, setAddError] = useState('');

    const [itemToRemove, setItemToRemove] = useState<DndListItem | null>(null);

    const activeConfig = TAB_CONFIG.find(t => t.key === activeTab)!;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getDndList({
                type: activeTab,
                page,
                page_size: PAGE_SIZE,
                search: searchQuery,
            });
            setItems(data.items);
            setTotal(data.total);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load list';
            setError(message);
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [activeTab, page, searchQuery]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value.trim());
                setPage(1);
            }, 400),
        [],
    );

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const handleTabChange = (tab: DndListType) => {
        setActiveTab(tab);
        setPage(1);
        setSearchInput('');
        setSearchQuery('');
        setError('');
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        if (!value.trim()) {
            debouncedSetSearch.cancel();
            setSearchQuery('');
            setPage(1);
        } else {
            debouncedSetSearch(value);
        }
    };

    const handleAdd = async () => {
        const trimmed = addValue.trim();
        if (activeTab === 'email') {
            if (!trimmed) {
                setAddError('Please enter an email address.');
                return;
            }
            if (!EMAIL_REGEX.test(trimmed)) {
                setAddError('Please enter a valid email address.');
                return;
            }
        } else {
            if (!INDIAN_PHONE_REGEX.test(trimmed)) {
                setAddError('Please enter a valid 10-digit Indian mobile number (starting with 6–9).');
                return;
            }
        }
        setActionLoading(true);
        setAddError('');
        try {
            if (activeTab === 'email') {
                await addDndEmail(trimmed);
            } else {
                await addDndPhone(trimmed);
            }
            setShowAddModal(false);
            setAddValue('');
            setPage(1);
            await fetchList();
        } catch (err: unknown) {
            setAddError(err instanceof Error ? err.message : 'Failed to add entry');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemove = async () => {
        if (!itemToRemove) return;
        setActionLoading(true);
        try {
            if (activeTab === 'email') {
                await removeDndEmail(itemToRemove.id);
            } else {
                await removeDndPhone(itemToRemove.id);
            }
            setItemToRemove(null);
            if (items.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            } else {
                await fetchList();
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove entry');
            setItemToRemove(null);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadCsv = async () => {
        setActionLoading(true);
        setError('');
        try {
            const blob = await downloadDndCsv(activeTab);
            triggerCsvDownload(blob, `dnd_${activeTab}_list.csv`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to download CSV');
        } finally {
            setActionLoading(false);
        }
    };

    const paginationItems = useMemo(() => {
        const pages: number[] = [];
        const maxVisible = 7;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [page, totalPages]);

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ color: '#000', marginBottom: '16px' }}>DND List</h2>

            <Nav variant="tabs" activeKey={activeTab} onSelect={key => key && handleTabChange(key as DndListType)}>
                {TAB_CONFIG.map(tab => (
                    <Nav.Item key={tab.key}>
                        <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 mb-3"
            >
                <Form.Control
                    type="search"
                    placeholder={`Search ${activeConfig.column.toLowerCase()}...`}
                    value={searchInput}
                    onChange={handleSearchChange}
                    style={{ maxWidth: '360px' }}
                />
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-primary"
                        onClick={handleDownloadCsv}
                        disabled={actionLoading}
                    >
                        Download CSV
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setAddValue('');
                            setAddError('');
                            setShowAddModal(true);
                        }}
                        disabled={actionLoading}
                    >
                        Add {activeTab === 'email' ? 'Email' : 'Phone Number'}
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>{activeConfig.column}</th>
                                <th style={{ width: '120px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr>
                                    <td colSpan={2} className="text-center text-muted">
                                        No entries found
                                    </td>
                                </tr>
                            ) : (
                                items.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.value}</td>
                                        <td>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => setItemToRemove(item)}
                                                disabled={actionLoading}
                                            >
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span className="text-muted">
                            Showing {items.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
                            {(page - 1) * PAGE_SIZE + items.length} of {total.toLocaleString()}
                        </span>
                        <Pagination className="mb-0">
                            <Pagination.First
                                disabled={page === 1}
                                onClick={() => setPage(1)}
                            />
                            <Pagination.Prev
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                            />
                            {paginationItems.map(p => (
                                <Pagination.Item
                                    key={p}
                                    active={p === page}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            />
                            <Pagination.Last
                                disabled={page >= totalPages}
                                onClick={() => setPage(totalPages)}
                            />
                        </Pagination>
                    </div>
                </>
            )}

            <Modal show={showAddModal} onHide={() => !actionLoading && setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add {activeConfig.column}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {addError && <Alert variant="danger">{addError}</Alert>}
                    <Form.Group>
                        <Form.Label>{activeConfig.column}</Form.Label>
                        <Form.Control
                            type={activeTab === 'email' ? 'email' : 'tel'}
                            value={addValue}
                            onChange={e => {
                                if (activeTab === 'phone') {
                                    setAddValue(e.target.value.replace(/\D/g, '').slice(0, 10));
                                } else {
                                    setAddValue(e.target.value);
                                }
                            }}
                            placeholder={
                                activeTab === 'email'
                                    ? 'user@example.com'
                                    : '9876543210'
                            }
                            maxLength={activeTab === 'phone' ? 10 : undefined}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        />
                        {activeTab === 'phone' && (
                            <Form.Text className="text-muted">
                                10-digit Indian mobile number only (no country code).
                            </Form.Text>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAdd} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" animation="border" /> : 'Add'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!itemToRemove} onHide={() => !actionLoading && setItemToRemove(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Remove <strong>{itemToRemove?.value}</strong> from the DND list?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setItemToRemove(null)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleRemove} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" animation="border" /> : 'Remove'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DndList;
